import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { LoginOptionDTO } from '@six-pockets/shared';
import { authApi } from '@/api';
import { Navigate, useLocation } from 'react-router';
import { EyeIcon, EyeOffIcon, LogoMark } from '@/components/icons';
import { Avatar, Button, InlineAlert, SelectField, TextField } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import styles from '@/styles/pages/LoginPage/style.scss';

export function LoginPage() {
  const { user, signIn } = useAuth();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [logins, setLogins] = useState<LoginOptionDTO[]>([]);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    authApi.logins().then(setLogins, () => setError('Could not reach the server. Try again.'));
  }, []);

  if (user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError(username ? 'Enter your password' : 'Select a user and enter the password');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await signIn(username, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className={styles.page}>
      <form className={styles.card} onSubmit={onSubmit} noValidate aria-label="Sign in">
        <div className={styles.brand}>
          <LogoMark size={64} />
        </div>

        <SelectField
          label="User"
          placeholder="Select user"
          autoFocus
          options={logins.map((l) => ({
            value: l.username,
            label: l.displayName,
            description: l.username,
            leading: <Avatar name={l.displayName} size="sm" />,
          }))}
          value={username}
          onChange={(value) => {
            setUsername(value);
            setError(null);
            passwordRef.current?.focus();
          }}
        />
        {/* Lets password managers pair the saved password with the chosen user. */}
        <input type="text" name="username" autoComplete="username" value={username} readOnly hidden />
        <TextField
          ref={passwordRef}
          label="Password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          suffix={
            <Button
              iconOnly
              size="sm"
              variant="ghost"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              title={showPassword ? 'Hide password' : 'Show password'}
              icon={showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
              onClick={() => setShowPassword((v) => !v)}
            />
          }
        />

        {error && <InlineAlert message={error} />}

        <Button type="submit" variant="primary" size="lg" fullWidth disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </main>
  );
}
